import React from 'react'
import NavbarComponent from '../components/navbar'
import Footer from '../components/footer'

const ProjectsPage = () => {
    return (
        <>
            <NavbarComponent />
            <main className="ocn-wrap">
                <div className="ocn-pagehead">
                    <p className="ocn-eyebrow">Projects</p>
                    <h1>Research using OpenCodeNet labels</h1>
                    <p>
                        Expert-labelled commits are used to train machine learning models that score code contributions
                        at scale. These studies use the labels to measure novelty in open source software and to test
                        how governance and collaboration shape it.
                    </p>
                </div>

                <article className="ocn-card ocn-paper">
                    <div>
                        <div className="venue">
                            <span className="ocn-tag">Published</span>
                            <span><i>Research Policy</i> 54(8), 105284 · 2025 · Open access</span>
                        </div>
                        <h2 style={{ marginTop: '0.6rem' }}>
                            Write access provisioning and organizational ownership in open source software projects:
                            Exploring the impact on project novelty and survival
                        </h2>
                        <p className="authors">Poonacha K. Medappa, Shirish C. Srivastava, Saverio D. Favaron</p>
                    </div>

                    <div className="ocn-keyfigs">
                        <div><b>2,400</b><span>commits rated by five expert programmers</span></div>
                        <div><b>5,762</b><span>matched GitHub projects analysed</span></div>
                        <div><b>0.71</b><span>precision of the novelty classifier</span></div>
                    </div>

                    <div className="ocn-paper-cols">
                        <div>
                            <h3>How the labels are used</h3>
                            <ul>
                                <li>Five expert programmers with open source experience rated a random sample of 2,400 commits for novelty on a 1–5 scale, after a pilot study. Agreement was checked with Krippendorff’s alpha.</li>
                                <li>The experts saw each commit’s description, change details (lines added or deleted, files changed, comments, parents) and the code itself.</li>
                                <li>A multi-layer perceptron trained on these labels scored the novelty of every contribution across the sample, more than a million commits that would have been impractical to rate by hand.</li>
                                <li>The same approach measured commit usefulness and whether a commit addressed a bug or a feature request, used as controls.</li>
                            </ul>
                        </div>
                        <div>
                            <h3>Key results</h3>
                            <ul>
                                <li>Giving a larger share of contributors write access to the source code increases the novelty of a project’s contributions.</li>
                                <li>The same openness raises the risk that the project becomes inactive, so it reduces survival.</li>
                                <li>Organization ownership strengthens the novelty gain and offsets the survival risk.</li>
                                <li>Contributors with write access drive innovation by selecting and integrating new ideas, while those without write access are crucial for reliability and long-term survival. Results hold with instrumental variables and matching.</li>
                            </ul>
                        </div>
                    </div>

                    <div className="venue">
                        <a className="btn btn-primary" href="https://doi.org/10.1016/j.respol.2025.105284" target="_blank" rel="noopener noreferrer">Read the paper ↗</a>
                        <a className="btn btn-outline-secondary" href="https://github.com/km-Poonacha/ResearchPolicy_Write_Access_Provisioning" target="_blank" rel="noopener noreferrer">Training data ↗</a>
                    </div>
                </article>

                <article className="ocn-card ocn-paper">
                    <div>
                        <div className="venue">
                            <span className="ocn-tag">Working paper</span>
                            <span>Under review, <i>European Journal of Information Systems</i></span>
                        </div>
                        <h2 style={{ marginTop: '0.6rem' }}>
                            From source code to concept: Measuring implementation novelty in software artifacts
                        </h2>
                        <p className="authors">P.K. Medappa, S.C. Srivastava, A. Saghafi</p>
                    </div>

                    <p>
                        Two programs can deliver the same functionality while differing in how original their structure,
                        architecture and algorithms are. The paper calls this <b>implementation novelty</b> and shows how to
                        measure it at scale, which matters more as AI tools make functionally correct code easy to produce.
                    </p>

                    <figure className="ocn-figure">
                        <img src="/figures/implementation-novelty-approach.png" alt="Two-step approach: code snippets are turned into fixed-length code vectors from their abstract syntax tree paths, then machine learning models trained on expert judgements predict the novelty of new code." loading="lazy" />
                        <figcaption>Figure 1. The two-step approach: (1) represent code as vectors learned from its abstract syntax tree paths; (2) train models on expert judgements to predict novelty for a large sample.</figcaption>
                    </figure>

                    <div className="ocn-keyfigs">
                        <div><b>2,000</b><span>commits from 548 Java projects rated by five experts</span></div>
                        <div><b>0.61</b><span>macro-F1 with code vectors</span></div>
                        <div><b>0.44</b><span>macro-F1 of the best text-only model</span></div>
                    </div>

                    <div className="ocn-paper-cols">
                        <div>
                            <h3>How the labels are used</h3>
                            <ul>
                                <li>After a 10-commit pilot and a 100-commit validation study, five experts labelled a random sample of 2,000 commits. Agreement was confirmed with a Krippendorff’s alpha above 0.70.</li>
                                <li>For each commit, experts first chose its type (peer review, documentation, bug fix, feature, process change or testing), then rated its novelty from 1 to 5.</li>
                                <li>Each commit’s code is parsed into an abstract syntax tree and turned into a code vector. Classifiers trained on the expert labels then predict novelty for every commit in the sample.</li>
                            </ul>
                        </div>
                        <div>
                            <h3>Key results</h3>
                            <ul>
                                <li>Code vectors predict expert ratings better than text-only models across all model families tested.</li>
                                <li>Projects with more diverse contributions produce more novel implementations.</li>
                                <li>Implementation novelty increases code reuse by other projects and carries about a third of diversity’s effect on reuse.</li>
                                <li>More novel code is less likely to be accepted as a pull request, which suggests non-standard implementations are harder to review and integrate.</li>
                            </ul>
                        </div>
                    </div>
                </article>
            </main>
            <Footer />
        </>
    )
}

export default ProjectsPage
